import { SearchOutlined } from '@ant-design/icons'
import { Button, Flex, Input, message, Select, Table, Tag } from 'antd'
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table'
import dayjs from 'dayjs'
import {
  PromoCodeDistributionEmailStatus,
  PromoCodeDistributionItem,
  PromoCodeDistributionType,
  PromoCodeItem,
} from 'Modules'
import { useCallback, useMemo, useState } from 'react'

import { DEFAULT_PAGE_OPTIONS, DEFAULT_PAGE_SIZE } from 'constants/pagination'
import {
  DISTRIBUTION_EMAIL_STATUS_OPTIONS,
  DISTRIBUTION_TYPE_LABEL,
  DISTRIBUTION_TYPE_OPTIONS,
} from 'constants/promo-code'
import { useDistributions, useResendDistribution } from 'hooks/promo-code'
import { useDebounce } from 'hooks/use-debounce'

interface HistoryTabProps {
  promoCode: PromoCodeItem | null
  canEdit: boolean
  onClose: () => void
}

const DATE_TIME_FORMAT = 'DD/MM/YYYY HH:mm'
const SEARCH_DEBOUNCE_MS = 400

/** Whitelist & Email — History tab: one row per Save & Send Email action. */
export const HistoryTab = ({ promoCode, canEdit, onClose }: HistoryTabProps) => {
  const [searchValue, setSearchValue] = useState('')
  const debouncedSearch = useDebounce(searchValue.trim(), SEARCH_DEBOUNCE_MS)
  const [typeFilter, setTypeFilter] = useState<PromoCodeDistributionType | undefined>(undefined)
  const [statusFilter, setStatusFilter] = useState<PromoCodeDistributionEmailStatus | undefined>(
    undefined
  )
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)

  const { distributions, total, isLoading } = useDistributions(promoCode, {
    keyword: debouncedSearch || undefined,
    type: typeFilter,
    emailStatus: statusFilter,
    page,
    limit: pageSize,
  })

  // Unfiltered first record = the latest save overall (list is createdAt desc);
  // also supplies the "of {total} records" denominator.
  const overall = useDistributions(promoCode, { page: 1, limit: DEFAULT_PAGE_SIZE })
  const latestId = overall.distributions[0]?.id

  const resendMutation = useResendDistribution(promoCode?.id)
  const [resendingId, setResendingId] = useState<string | null>(null)

  // Emails (incl. resend) can only go out for an Active code.
  const isActive = promoCode?.status === 'ACTIVE'

  const { mutate: resend } = resendMutation
  const handleResend = useCallback(
    (record: PromoCodeDistributionItem) => {
      setResendingId(record.id)
      resend(record.id, {
        onSuccess: (data) => {
          message.success(`Resent ${data?.resend ?? record.failedCount} email(s)`)
        },
        onError: (error: any) => {
          message.error(
            error?.response?.data?.message || error?.message || 'Failed to resend email'
          )
        },
        onSettled: () => setResendingId(null),
      })
    },
    [resend]
  )

  const columns: ColumnsType<PromoCodeDistributionItem> = useMemo(
    () => [
      {
        title: 'Date & Time',
        dataIndex: 'createdAt',
        key: 'createdAt',
        width: 180,
        render: (value: string, record) => (
          <Flex align="center" gap={6}>
            <span>{value ? dayjs(value).format(DATE_TIME_FORMAT) : '-'}</span>
            {record.id === latestId && <Tag color="blue">Latest</Tag>}
          </Flex>
        ),
      },
      {
        title: 'Type',
        dataIndex: 'type',
        key: 'type',
        width: 130,
        render: (type: PromoCodeDistributionType) => DISTRIBUTION_TYPE_LABEL[type],
      },
      {
        title: 'Users',
        dataIndex: 'totalUsers',
        key: 'totalUsers',
        width: 80,
      },
      {
        title: 'Subject',
        dataIndex: 'subject',
        key: 'subject',
        ellipsis: true,
        render: (subject?: string) => subject || '–',
      },
      {
        title: 'Email Status',
        key: 'emailStatus',
        width: 150,
        render: (_, record) => {
          if (record.emailStatus === 'NOT_SENT') {
            return <span className="whitelist-email__status-muted">Not sent</span>
          }
          if (record.emailStatus === 'SENDING') {
            return <span className="whitelist-email__status-sending">Sending…</span>
          }
          return (
            <div>
              {record.sentCount > 0 && (
                <div className="whitelist-email__status-sent">{record.sentCount} sent</div>
              )}
              {record.failedCount > 0 && (
                <div className="whitelist-email__status-failed">{record.failedCount} failed</div>
              )}
            </div>
          )
        },
      },
      {
        title: '',
        key: 'actions',
        width: 120,
        // Resend only applies once sending has finished with failures (partial/failed).
        render: (_, record) =>
          canEdit && isActive && record.emailStatus !== 'SENDING' && record.failedCount > 0 ? (
            <Button
              size="small"
              onClick={() => handleResend(record)}
              loading={resendingId === record.id}
            >
              Resend ({record.failedCount})
            </Button>
          ) : null,
      },
    ],
    [latestId, canEdit, isActive, resendingId, handleResend]
  )

  const handleTableChange = (pagination: TablePaginationConfig) => {
    setPage(pagination.current ?? 1)
    setPageSize(pagination.pageSize ?? DEFAULT_PAGE_SIZE)
  }

  return (
    <div className="whitelist-email__history">
      <Flex gap={12} wrap="wrap" className="whitelist-email__history-toolbar">
        <Input
          prefix={<SearchOutlined />}
          placeholder="Search by subject"
          value={searchValue}
          onChange={(e) => {
            setSearchValue(e.target.value)
            setPage(1)
          }}
          allowClear
          style={{ flex: 1, minWidth: 180 }}
        />
        <Select<PromoCodeDistributionType | 'ALL'>
          value={typeFilter ?? 'ALL'}
          onChange={(value) => {
            setTypeFilter(value === 'ALL' ? undefined : value)
            setPage(1)
          }}
          options={[{ value: 'ALL', label: 'All types' }, ...DISTRIBUTION_TYPE_OPTIONS]}
          style={{ width: 150 }}
        />
        <Select<PromoCodeDistributionEmailStatus | 'ALL'>
          value={statusFilter ?? 'ALL'}
          onChange={(value) => {
            setStatusFilter(value === 'ALL' ? undefined : value)
            setPage(1)
          }}
          options={[{ value: 'ALL', label: 'All statuses' }, ...DISTRIBUTION_EMAIL_STATUS_OPTIONS]}
          style={{ width: 160 }}
        />
      </Flex>

      <Table<PromoCodeDistributionItem>
        className="whitelist-email__history-table"
        columns={columns}
        dataSource={distributions}
        rowKey={(record) => record.id}
        loading={isLoading}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          pageSizeOptions: DEFAULT_PAGE_OPTIONS,
        }}
        onChange={handleTableChange}
      />

      <Flex justify="space-between" align="center" className="whitelist-email__footer">
        <span className="whitelist-email__record-count">
          {total} of {overall.total} records
        </span>
        <Button onClick={onClose}>Close</Button>
      </Flex>
    </div>
  )
}
